import { useSimStore } from '../store/useSimStore';

/**
 * Shared sync payload logic — single source of truth for BOTH transports
 * (BroadcastChannel same-device sync and WebSocket two-device LAN sync), so a
 * field added to one can never silently go missing from the other.
 *
 * NOTE: alarms are intentionally NOT synced. Each monitor display evaluates its
 * own alarms from the synced vitals (see MonitorDisplay). The instructor
 * (publisher) never evaluates alarms, so broadcasting its empty list would
 * strobe the monitor's real alarms off/on.
 */
// Stable per-tab id so monitors can detect two competing instructor panels.
export const INSTRUCTOR_ID = Math.random().toString(36).slice(2, 10);

export function buildPayload(state) {
  return {
    instructorId: INSTRUCTOR_ID,
    vitals: state.vitals,
    rhythm: state.rhythm,
    prevRhythm: state.prevRhythm,
    rhythmProgress: state.rhythmProgress,
    capnoMorphology: state.capnoMorphology,
    activeModifier: state.activeModifier,
    audioMuted: state.audioMuted,
    transitionDuration: state.transitionDuration,
    visibleChannels: state.visibleChannels,
    activeScenario: state.activeScenario,
    patientName: state.patientName,
    patientId: state.patientId,
    patientAge: state.patientAge,
    patientWeight: state.patientWeight,
    // NIBP is measured locally on the monitor (manual + q2.5-min auto), so it is
    // deliberately NOT synced — the instructor would otherwise overwrite it.
    tofCount: state.tofCount,
    tofRatio: state.tofRatio,
    bisValue: state.bisValue,
    activeMedia: state.activeMedia,
    activeAidCard: state.activeAidCard,
    splitScreen: state.splitScreen,
    intubated: state.intubated,
    ventSettings: state.ventSettings,
    ppv: state.ppv,
    fetalMonitor: state.fetalMonitor,
    eegMonitor: state.eegMonitor,
    fetal: state.fetal,
    audioCue: state.audioCue,
    // Case timer only (NOT the samples/events arrays) so the monitor can show
    // the same elapsed clock.
    caseActive: state.caseRecording.active,
    caseStartedAt: state.caseRecording.startedAt,
  };
}

/** Apply a received STATE_UPDATE payload to the local store (receiver side). */
export function applyPayload(payload) {
  // Use getState() to access store actions without a reactive subscription.
  const store = useSimStore.getState();
  store.noteInstructor(payload.instructorId);

  Object.entries(payload.vitals || {}).forEach(([vitalName, vital]) => {
    store.setVital(vitalName, vital);
  });

  // Apply rhythm + cross-fade state together (raw — no local reset) so the
  // monitor animates the same rhythm transition the instructor is driving.
  if (payload.rhythm !== undefined) store.applyRhythmSync(payload.rhythm, payload.prevRhythm, payload.rhythmProgress);
  if (payload.capnoMorphology !== undefined) store.setCapnoMorphology(payload.capnoMorphology);
  if (payload.activeModifier !== undefined) store.setActiveModifier(payload.activeModifier);
  if (payload.audioMuted !== undefined) store.setAudioMuted(payload.audioMuted);
  if (payload.transitionDuration !== undefined) store.setTransitionDuration(payload.transitionDuration);
  if (payload.visibleChannels !== undefined) store.setVisibleChannels(payload.visibleChannels);
  if (payload.activeScenario !== undefined) store.setActiveScenario(payload.activeScenario);
  if (payload.patientName !== undefined) store.setPatientField('patientName', payload.patientName);
  if (payload.patientId !== undefined) store.setPatientField('patientId', payload.patientId);
  if (payload.patientAge !== undefined || payload.patientWeight !== undefined) {
    store.setPatientInfo(payload.patientAge, payload.patientWeight);
  }
  if (payload.tofCount !== undefined && payload.tofRatio !== undefined) store.setTof(payload.tofCount, payload.tofRatio);
  if (payload.bisValue !== undefined) store.setBIS(payload.bisValue);
  if (payload.activeMedia !== undefined) store.setActiveMedia(payload.activeMedia);
  if (payload.activeAidCard !== undefined) store.setActiveAidCard(payload.activeAidCard);
  if (payload.splitScreen !== undefined) store.setSplitScreen(payload.splitScreen);
  if (payload.intubated !== undefined) store.setIntubated(payload.intubated);
  if (payload.ventSettings !== undefined) store.setVentSettings(payload.ventSettings);
  if (payload.ppv !== undefined) store.setPpv(payload.ppv);
  if (payload.fetalMonitor !== undefined) store.setFetalMonitor(payload.fetalMonitor);
  if (payload.eegMonitor !== undefined) store.setEegMonitor(payload.eegMonitor);
  if (payload.fetal !== undefined) store.setFetal(payload.fetal);
  if (payload.audioCue !== undefined) store.setAudioCue(payload.audioCue);
  if (payload.caseActive !== undefined) store.setCaseTimer(payload.caseActive, payload.caseStartedAt);
}
